import {Reducer} from 'redux';
import initialState from './state';
import {IReduxAction, IReduxModelConfigsState} from '@src/@interfaces';
import {
    MODEL_CONFIGS_CANCEL_FETCH,
    MODEL_CONFIGS_CLEAR,
    MODEL_CONFIGS_FETCH,
    MODEL_CONFIGS_RETRY_FETCH,
    MODEL_CONFIGS_SAVE_ERROR,
    MODEL_CONFIGS_SET,
    MODEL_CONFIGS_SET_PRELOADED_CONFIGS
} from './constants';
import {deepCopy} from '@src/helpers/deepCopy';
import {stringifyDate} from '@src/helpers/dates';

const reducer: Reducer = (state: IReduxModelConfigsState = initialState, action: IReduxAction): IReduxModelConfigsState => {
    switch (action.type) {
        case MODEL_CONFIGS_FETCH: {
            return {
                ...state,
                nowLoadingModelConfigs: {
                    ...state.nowLoadingModelConfigs,
                    [action.payload]: true
                }
            };
        }

        case MODEL_CONFIGS_RETRY_FETCH: {
            const newState = deepCopy(state);
            const controllerName = action.payload;

            newState.nowLoadingModelConfigs[controllerName] = true;

            if (newState.errors[controllerName]) {
                newState.errors[controllerName].attempts = 0;
            }

            return newState;
        }

        case MODEL_CONFIGS_CANCEL_FETCH: {
            const newState = deepCopy(state);
            const controllerName = action.payload;

            if (controllerName in newState.nowLoadingModelConfigs) {
                delete newState.nowLoadingModelConfigs[controllerName];
            }

            if (controllerName in newState.errors) {
                delete newState.errors[controllerName];
            }

            return newState;
        }

        case MODEL_CONFIGS_SET_PRELOADED_CONFIGS: {
            const newState = deepCopy(state);
            type Key = keyof Pick<IReduxModelConfigsState, 'listConfigs' | 'formConfigs' | 'importConfigs'>;
            for (const prop of ['listConfigs', 'formConfigs', 'importConfigs'] as Key[]) {
                if (action.payload[prop]) {
                    newState[prop] = {...newState[prop], ...action.payload[prop]};
                }
            }

            newState.updatedAt = Date.now();

            return newState;
        }

        case MODEL_CONFIGS_SET: {
            const newState = deepCopy(state);
            const controllerName = action.payload.controllerName;

            if (action.payload.configs['list-config']) {
                newState.listConfigs[controllerName] = action.payload.configs['list-config'];
            }

            if (action.payload.configs['form-config']) {
                newState.formConfigs[controllerName] = action.payload.configs['form-config'];
            }

            if (action.payload.configs['import-config']) {
                newState.importConfigs[controllerName] = action.payload.configs['import-config'];
            }

            if (controllerName in newState.nowLoadingModelConfigs) {
                delete newState.nowLoadingModelConfigs[controllerName];
            }

            if (controllerName in newState.errors) {
                delete newState.errors[controllerName];
            }

            newState.updatedAt = Date.now();

            return newState;
        }

        case MODEL_CONFIGS_SAVE_ERROR: {
            const newState = deepCopy(state);
            const controllerName = action.payload.controllerName;

            if (controllerName in newState.nowLoadingModelConfigs) {
                delete newState.nowLoadingModelConfigs[controllerName];
            }

            if (!(controllerName in newState.errors)) {
                newState.errors[controllerName] = {
                    error: action.payload.error,
                    attempts: 1,
                    totalAttempts: 1,
                    lastAttemptAt: stringifyDate(),
                };
            } else {
                newState.errors[controllerName].error = action.payload.error;
                newState.errors[controllerName].attempts++;
                newState.errors[controllerName].totalAttempts++;
                newState.errors[controllerName].lastAttemptAt = stringifyDate();
            }

            return newState;
        }

        case MODEL_CONFIGS_CLEAR: {
            return {
                ...state,
                formConfigs: {},
                listConfigs: {},
                nowLoadingModelConfigs: {},
                errors: {},
                updatedAt: 0,
            };
        }

        default:
            break;
    }

    return state;
};

export default reducer;
